import { TransactionItemFormType } from '@/features/transaction_items/List/type';
import { useTranslation } from '@/i18n';
import yup from '@/libs/validation';
import Big from 'big.js';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

const MAX_NUMERIC_STRING_LENGTH = 35;

const castNumericValue = (value: string | number | undefined | null) => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }

  return value.toString();
};

const hasDecimalPoint = (value: string | undefined): boolean => {
  if (!value) return false;
  return value.includes('.');
};

const isNumericLengthValid = (value: string | undefined): boolean => {
  if (!value) return true;
  return (value?.replace(/[-,]/g, '').length ?? 0) <= MAX_NUMERIC_STRING_LENGTH;
};

export const useMinAndMaxHandlers = () => {
  const { t } = useTranslation();
  const { getValues, setValue, setError, clearErrors, watch } =
    useFormContext<TransactionItemFormType>();

  const schema = useMemo(
    () =>
      yup.object().shape({
        min_value: yup
          .string()
          .test(
            'maxLength',
            t('transaction_items.validation_message.numeric.maxLength', {
              maxLength: MAX_NUMERIC_STRING_LENGTH,
            }),
            (value) => {
              return isNumericLengthValid(value);
            }
          )
          .test(
            'decimalNotAllowed',
            t('transaction_items.validation_message.decimalNotAllowed'),
            (value) => {
              if (value && hasDecimalPoint(value)) {
                return false;
              }
              return true;
            }
          ),
        max_value: yup
          .string()
          .test(
            'maxLength',
            t('transaction_items.validation_message.numeric.maxLength', {
              maxLength: MAX_NUMERIC_STRING_LENGTH,
            }),
            (value) => {
              return isNumericLengthValid(value);
            }
          )
          .test(
            'decimalNotAllowed',
            t('transaction_items.validation_message.decimalNotAllowed'),
            (value) => {
              if (value && hasDecimalPoint(value)) {
                return false;
              }
              return true;
            }
          )
          .test(
            'maxGreaterThanMin',
            t('transaction_items.validation_message.maxGreaterThanMin.numeric'),
            (value, context) => {
              if (
                context.parent.min_value !== undefined &&
                value !== undefined
              ) {
                const minNum = Big(context.parent.min_value);
                const maxNum = Big(value);

                if (maxNum.cmp(minNum) <= 0) {
                  return false;
                }
              }
              return true;
            }
          ),
      }),
    [t]
  );

  const validate = useCallback(() => {
    const { min_value, max_value } = getValues();

    try {
      clearErrors('min_value');
      clearErrors('max_value');

      schema.validateSync(
        {
          min_value,
          max_value,
        },
        {
          abortEarly: false,
        }
      );
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        error.inner.forEach((error) => {
          if (error.path === 'min_value') {
            setError('min_value', {
              type: error.type,
              message: error.message,
            });
          }

          if (error.path === 'max_value') {
            setError('max_value', {
              type: error.type,
              message: error.message,
            });
          }
        });
      }
    }
  }, [schema, getValues, setError, clearErrors]);

  const minValue = watch('min_value');
  const maxValue = watch('max_value');

  const onChangeMinValue = useCallback(
    (value: string | number | null) => {
      setValue('min_value', castNumericValue(value), { shouldDirty: true });
      validate();
    },
    [setValue, validate]
  );

  const onChangeMaxValue = useCallback(
    (value: string | number | null) => {
      setValue('max_value', castNumericValue(value), { shouldDirty: true });
      validate();
    },
    [setValue, validate]
  );

  return { minValue, maxValue, onChangeMinValue, onChangeMaxValue };
};
